<script setup>
import { format, parseISO } from "date-fns";

const toast = useToast();
const isLoading = ref(false);

const {
  webinar,
  isMini = false,
  hideBorder = false,
} = defineProps({
  webinar: Object,
  isMini: {
    type: Boolean,
    default() {
      return false;
    },
  },
  hideBorder: {
    type: Boolean,
    default() {
      return false;
    },
  },
});

const { status } = useAuth();
const { data: studentWebinars } = useApiFetch("webinarBystudent", {
  method: "GET",
  key: "upcoming-webinars-by-student",
  queryKey: "upcoming-webinars-by-student",
  query: {
    status: "upcoming",
  },
  transform(webinars) {
    return webinars?.data;
  },
  onResponse({ response }) {
    if (response.status === 401) {
      return;
    }
  },
  onResponseError({ response }) {
    if (response.status === 401) {
      return;
    }
  },
});

const formattedDate = computed(() => {
  const parsedDate = parseISO(webinar.start_time);
  return format(parsedDate, "dd MMMM yyyy");
});

const formattedTime = computed(() => {
  const parsedDate = parseISO(webinar.start_time);
  return format(parsedDate, "hh:mm a");
});

const isJoined = computed(() => {
  return (
    studentWebinars?.value?.map((wb) => wb.id)?.includes(webinar.id) || false
  );
});

const joinWebinar = async () => {
  if (webinar.meeting_link) {
    navigateTo(webinar.meeting_link, {
      external: true,
      open: {
        target: "_blank",
      },
    });
  } else {
    toast.add({
      severity: "danger",
      summary: "Webinar does not have meeting link.",
      life: 3000,
    });
  }
};

const registerWebinar = async () => {
  if (status.value !== "authenticated") return;

  isLoading.value = true;
  await useApiFetch(`webinars/${webinar.id}/register`, {
    method: "POST",
    async onResponse() {
      await refreshNuxtData("upcoming-webinars-by-student");
      toast.add({
        severity: "success",
        summary: "You've joined the webinar.",
        life: 3000,
      });
    },
  });
  isLoading.value = false;
};
</script>
<template>
  <div
    :class="{
      'p-4 overflow-hidden border-slate-200 bg-white drop-shadow-sm rounded-xl': true,
      'border-0': isMini || hideBorder,
      'border-2 rounded-lg': !isMini && !hideBorder,
    }"
  >
    <div class="flex flex-col sm:flex-row gap-4">
      <div v-if="!isMini">
        <img
          v-if="webinar?.media?.length > 0"
          :src="webinar?.media?.at(0)?.original_url"
          alt="dummy"
          class="w-full h-52 sm:w-48 sm:h-40 rounded-lg opacity-80 object-cover"
        />
        <img
          v-else
          src="~/assets/img/webinar-dummy.jpg"
          alt="dummy"
          class="size-48 rounded-lg object-cover"
        />
      </div>
      <div class="text-body flex-1 flex flex-col justify-between">
        <div :class="[isMini ? 'space-y-2' : 'space-y-4']">
          <h3 class="md:text-xl text-lg font-semibold">
            {{ webinar?.title }}
          </h3>
          <div class="flex flex-col sm:flex-row gap-2 sm:gap-4">
            <div class="flex gap-3 items-center">
              <i class="pi pi-calendar"></i>
              <p class="text-sm">{{ formattedDate }}</p>
            </div>
            <div class="flex gap-3 items-center">
              <i class="pi pi-clock"></i>
              <p class="text-sm">{{ formattedTime }}</p>
            </div>
          </div>
          <div
            class="flex gap-1 items-center"
            v-if="webinar?.user_count && webinar?.user_count > 0"
          >
            <div class="flex">
              <div v-for="user in webinar?.users" :key="'webinar_' + user.id">
                <Avatar
                  v-if="user?.profile?.media?.at(0)?.original_url"
                  class="-ml-2 border-2 border-white"
                  :image="user?.profile?.media?.at(0)?.original_url"
                  shape="circle"
                />
                <Avatar
                  v-else
                  class="-ml-2 border-2 border-white"
                  :name="user?.name"
                  shape="circle"
                />
              </div>
            </div>
            <p class="text-sm text-black">
              {{ webinar.user_count }}
              {{ webinar.user_count === 1 ? "student" : "students" }} joining
              this webinar
            </p>
          </div>
        </div>
        <div class="text-right mt-6">
          <Button
            v-if="isJoined"
            label="Joined"
            icon="pi pi-angle-right"
            icon-pos="right"
            outlined
            :size="isMini ? 'small' : null"
            @click="joinWebinar"
          ></Button>
          <ProfileCompleteGuard @onCallback="registerWebinar" v-else>
            <Button
              label="Register"
              icon="pi pi-angle-right"
              icon-pos="right"
              :loading="isLoading"
              :size="isMini ? 'small' : null"
              @click="registerWebinar"
            ></Button>
          </ProfileCompleteGuard>
        </div>
      </div>
    </div>
  </div>
</template>
